import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalClose } from '../hooks/useModalClose';

function EditItemCheck({ item, theme, onClose, onSave }) {
    const { t } = useLanguage();
    useModalClose(onClose);
    const [discountType, setDiscountType] = useState(item.discount && Number(item.discount) > 0 ? 'value' : 'none'); // 'none', 'percent', 'value'
    const [discountAmount, setDiscountAmount] = useState(item.discount && Number(item.discount) > 0 ? Number(item.discount).toString() : '');
    const [deleteReason, setDeleteReason] = useState('');
    const [deleteNote, setDeleteNote] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const deleteReasons = [
        t('customerChangedMind'),
        t('itemNotAvailable'),
        t('kitchenError'),
        t('customerComplaint'),
        t('wrongOrder'),
        t('other')
    ];

    const handleDiscountSubmit = async () => {
        setLoading(true);
        
        try {
            let finalDiscountAmount = 0;
            
            if (discountType === 'none') {
                // Set discount to 0 when no discount is selected
                finalDiscountAmount = 0;
            } else {
                if (!discountAmount || discountAmount < 0) {
                    alert(t('pleaseEnterValidDiscountAmount'));
                    setLoading(false);
                    return;
                }
                
                if (discountType === 'percent') {
                    if (discountAmount > 100) {
                        alert(t('discountPercentCannotExceed100'));
                        setLoading(false);
                        return;
                    }
                    // Calculate per-unit discount (percentage of unit price)
                    finalDiscountAmount = (Number(item.price) * Number(discountAmount)) / 100;
                } else {
                    // Fixed discount is already per-unit
                    finalDiscountAmount = Number(discountAmount);
                }
            }

            const response = await fetch(`${BACKEND_URL}/api/order-products/${item.id}/discount`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ discount_amount: finalDiscountAmount })
            });

            if (!response.ok) throw new Error(t('failedToApplyDiscount'));
            
            onSave();
        } catch (err) {
            alert(t('failedToApplyDiscount') + ': ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteReason) {
            alert(t('pleaseSelectReasonForDeletion'));
            return;
        }

        if (deleteReason === 'Other' && !deleteNote.trim()) {
            alert(t('pleaseProvideDetailsForOther'));
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/order-products/${item.id}/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    reason: deleteReason,
                    note: deleteReason === 'Other' ? deleteNote : null,
                    cancelled_by: 'current_user' // You might want to pass actual user info
                })
            });

            if (!response.ok) throw new Error(t('failedToCancelItem'));
            
            onSave();
        } catch (err) {
            alert(t('failedToCancelItem') + ': ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: theme?.colors?.surface || '#fff',
                    padding: '2rem',
                    borderRadius: theme?.borderRadius || 8,
                    boxShadow: theme?.boxShadow || '0 4px 16px rgba(0,0,0,0.2)',
                    maxWidth: '500px',
                    width: '90%',
                    maxHeight: '80vh',
                    overflowY: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h3 style={{ 
                    color: theme?.colors?.primary || '#1976d2', 
                    marginTop: 0,
                    marginBottom: '1rem'
                }}>
                    {t('editItem')}: {item.product_name}
                </h3>

                <div style={{ marginBottom: '1rem' }}>
                    <strong>{t('currentDetails')}:</strong><br />
                    {t('quantity')}: {item.quantity}<br />
                    {t('price')}: ${Number(item.price || 0).toFixed(2)} {t('each')}<br />
                    {t('total')}: ${(Number(item.price || 0) * item.quantity).toFixed(2)}<br />
                    {item.discount && `${t('currentDiscount')}: $${Number(item.discount).toFixed(2)}`}
                </div>

                {!showDeleteConfirm ? (
                    <>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{ color: theme?.colors?.text, marginBottom: '0.5rem' }}>{t('applyDiscount')}</h4>
                            
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        value="none"
                                        checked={discountType === 'none'}
                                        onChange={(e) => setDiscountType(e.target.value)}
                                        style={{ marginRight: '0.5rem' }}
                                    />
                                    {t('noDiscount')}
                                </label>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        value="percent"
                                        checked={discountType === 'percent'}
                                        onChange={(e) => setDiscountType(e.target.value)}
                                        style={{ marginRight: '0.5rem' }}
                                    />
                                    {t('discountByPercentage')}
                                </label>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        value="value"
                                        checked={discountType === 'value'}
                                        onChange={(e) => setDiscountType(e.target.value)}
                                        style={{ marginRight: '0.5rem' }}
                                    />
                                    {t('discountByFixedAmount')}
                                </label>
                            </div>

                            {discountType !== 'none' && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                                        {discountType === 'percent' ? t('discountPercentage') + ':' : t('discountAmount') + ' ($):'}
                                    </label>
                                    <input
                                        type="number"
                                        value={discountAmount}
                                        onChange={(e) => setDiscountAmount(e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        placeholder={discountType === 'percent' ? '10' : '5.00'}
                                        min="0"
                                        max={discountType === 'percent' ? '100' : undefined}
                                        step={discountType === 'percent' ? '1' : '0.01'}
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            border: '1px solid #ddd',
                                            borderRadius: theme?.borderRadius || 4,
                                            fontSize: '1rem'
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{ 
                            display: 'flex', 
                            gap: '1rem', 
                            justifyContent: 'space-between',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={handleDiscountSubmit}
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem 1rem',
                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 6,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontWeight: 'bold',
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                {loading ? t('applying') : t('applyChanges')}
                            </button>
                            
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem 1rem',
                                    backgroundColor: '#f44336',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 6,
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('deleteItem')}
                            </button>
                            
                            <button
                                onClick={onClose}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem 1rem',
                                    backgroundColor: theme?.colors?.secondary || '#666',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 6,
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('cancel')}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{ color: '#f44336', marginBottom: '1rem' }}>
                                {t('confirmItemDeletion')}
                            </h4>
                            <p style={{ marginBottom: '1rem' }}>
                                {t('areYouSureDeleteItemCannotUndo')}
                            </p>
                            
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('reasonForDeletion')}:
                            </label>
                            <select
                                value={deleteReason}
                                onChange={(e) => setDeleteReason(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #ddd',
                                    borderRadius: theme?.borderRadius || 4,
                                    fontSize: '1rem'
                                }}
                                required
                            >
                                <option value="">{t('selectReason')}</option>
                                {deleteReasons.map(reason => (
                                    <option key={reason} value={reason}>{reason}</option>
                                ))}
                            </select>
                            
                            {deleteReason === 'Other' && (
                                <div style={{ marginTop: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                        {t('pleaseProvideDetails')}:
                                    </label>
                                    <textarea
                                        value={deleteNote}
                                        onChange={(e) => setDeleteNote(e.target.value)}
                                        placeholder={t('explainWhyItemNeedsDeleted')}
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            border: '1px solid #ddd',
                                            borderRadius: theme?.borderRadius || 4,
                                            fontSize: '1rem',
                                            minHeight: '80px',
                                            resize: 'vertical'
                                        }}
                                        required
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
                            <button
                                onClick={handleDelete}
                                disabled={loading || !deleteReason || (deleteReason === 'Other' && !deleteNote.trim())}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem 1rem',
                                    backgroundColor: '#f44336',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 6,
                                    cursor: (loading || !deleteReason || (deleteReason === 'Other' && !deleteNote.trim())) ? 'not-allowed' : 'pointer',
                                    fontWeight: 'bold',
                                    opacity: (loading || !deleteReason || (deleteReason === 'Other' && !deleteNote.trim())) ? 0.6 : 1
                                }}
                            >
                                {loading ? t('deleting') : t('confirmDelete')}
                            </button>
                            
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                style={{
                                    flex: 1,
                                    padding: '0.75rem 1rem',
                                    backgroundColor: theme?.colors?.secondary || '#666',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 6,
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('back')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default EditItemCheck;